/**
 * Every explanatory sentence on the site, in one place, so the words a reader
 * sees beside a relationship are written once and read the same everywhere.
 * The reading page is the long form; each `Hint` is one line of it placed
 * where the relationship is shown. Plain words, full sentences, no invented
 * abbreviations; "shared-funder map" is the user-facing name of the
 * co-funding overlay.
 */
export const READING_SECTIONS = ['returns', 'grants', 'recipients', 'listing', 'amended', 'subjects', 'intermediaries', 'map', 'search', 'freshness'] as const;
export type ReadingSection = (typeof READING_SECTIONS)[number];

export const COPY = {
  site: {
    tagline: 'Who funds what, from public filings, with the source on every fact.',
    contact: 'hello@fundergraph.org',
    /** The header's link to the documentation; short, because the header also holds the search box at phone width. */
    about: 'About',
    footer: 'Facts come from IRS filings, funder websites, and federal award records, each cited where it appears. Identifiers under https://fundergraph.org/id/ name things, not pages. No individual is named anywhere in the graph.',
  },
  search: {
    placeholder: 'Find an organization by name or EIN',
    open: (ein: string): string => `Open ${ein}`,
    nothing: 'No organization by that name has a page.',
    why: 'Why is it not here?',
    unavailable: 'Search is unavailable.',
  },
  home: {
    headline: 'Who funds what',
    lead: 'FunderGraph reads every e-filed nonprofit return and the IRS master file, and holds each grant with the return that reported it. Look an organization up by name or EIN in the box above, or start from a subject.',
    examplesTitle: 'Three places to start',
    examples: [
      { ein: '522094677', name: 'Alliance for Global Justice', note: 'A fiscal sponsor: grants to the projects it hosts, and the funders behind them.' },
      { ein: '941156365', name: 'Stanford University', note: 'A large recipient and a funder in its own right, with a shared-funder map of its peers.' },
      { ein: '110303001', name: 'Fidelity Charitable', note: 'A grant intermediary: the largest grant list in the graph, set aside when funders are counted.' },
    ],
  },
  organization: {
    filings: 'Every return the IRS has released for this organization. Totals count the current return for each period; an amended return replaces the original.',
    masterFile: 'What the IRS records about this organization’s status in the Exempt Organizations master file.',
    grantsReceived: 'Grants other filers reported giving to this organization, from their returns. A resolved badge means the funder wrote a name, not a number, and FunderGraph matched it.',
    grantsGiven: 'What this organization reported giving, as written on its return. A recipient without a link has no return on record.',
    sponsoredProjects: 'Grants to a project are listed under the sponsor because the project files no return of its own.',
    intermediary: (ceiling: number): string =>
      `This organization gives to more than ${ceiling.toLocaleString('en-US')} distinct organizations, which is what donor-advised funds and similar pass-throughs do. It is shown everywhere and set aside where funders are counted.`,
    ninetyT: 'A 990-T reports unrelated business income and carries no grants.',
  },
  funder: {
    periods: 'Each row is one current return; the period is the month that fiscal year ended.',
    subjects: 'Where the money went, by the recipients’ IRS classification. Recipients without a classification are not counted here.',
    states: 'By the recipient’s state in the master file.',
    peers: 'Funders whose grant lists name the same organizations. A high count with an intermediary means both give widely, not that they coordinate.',
    grantsGiven: 'Every grant this funder reported, newest first.',
  },
  subject: {
    classification: 'Organizations are placed here by their IRS activity code (NTEE), mapped into this subject tree; the codes that map here are listed below.',
    funders: 'Funders whose grants reached organizations in this subject, including narrower subjects, in the chosen state. Intermediaries appear because their grant lists name the final recipient.',
    hideIntermediaries: 'Hide intermediaries',
    // The server orders a subject's pages by grant count, so a dollar sort can only reorder the page that is
    // loaded. The note says so rather than let the control pose as a ranking of the whole list; pages in
    // dollar order belong to the access-pattern work of phase 3.
    sortScope: (sort: 'grants' | 'total', pages: number): string | null =>
      sort === 'total' && pages > 1 ? 'Sorted by dollars within this page only; the pages are in order of grant count.' : null,
  },
  map: {
    title: 'Shared-funder map',
    what: (anchor: string, window: string, minimum: number, ceiling: number): string =>
      `Organizations that share at least ${minimum} funders with ${anchor}, from grants reported for tax years ${window.replace('-', ' to ')}. Computed from grant rows; it shows shared money, not shared purpose. Funders reaching more than ${ceiling.toLocaleString('en-US')} organizations are set aside.`,
    funders: 'Funders that reached the anchor in the window and were counted.',
    excluded: 'Funders that reached the anchor but reach more organizations than the ceiling; shown and not scored.',
    members: 'Score is the number of counted funders shared with the anchor.',
    mini: 'The funders that reached this organization and the organizations that share the most of them. The whole map has every one, and the method.',
    controls: 'Hover or select a funder to see the organizations it reaches; select an organization to see the funders it shares with the anchor.',
    emptyDetail: 'Select a funder or an organization to see what it shares.',
  },
  reading: {
    title: 'How FunderGraph reads a filing',
    sections: [
      {
        id: 'returns',
        heading: 'Returns and periods',
        body: 'Nonprofits file an annual return with the IRS: Form 990, 990-EZ, or, for private foundations, 990-PF. The IRS publishes the e-filed returns and FunderGraph reads every one. A tax period is the month an organization’s fiscal year ended; a return for August 2025 covers the year that ended that month. A 990-T reports unrelated business income and carries no grants.',
      },
      {
        id: 'grants',
        heading: 'Where grants come from',
        body: 'A grant edge is one row of a filer’s grant list: Schedule I on a Form 990, Part XV on a 990-PF. The funder wrote the row; FunderGraph shows it as written, with the return it came from, linked to the public copy of that return. Website grant lists and federal award records are read the same way and cite the page or award instead.',
      },
      {
        id: 'recipients',
        heading: 'Stated, resolved, and unplaced recipients',
        body: 'Some filers write the recipient’s EIN; most private foundations write only a name and a city. When a name matches one organization in the IRS master file in the same state, FunderGraph links it and marks the link resolved, with how it was matched: an exact name, a name-and-place rule, or the nearest name corroborated by place. A name that could not be placed is shown as the funder wrote it, and counts toward nothing but that funder’s own list.',
      },
      {
        id: 'listing',
        heading: 'Listed and unlisted',
        body: 'An organization has its own page when it files its own return, or when the master file records that it has filed one. A group that appears only inside another organization’s filing, a sponsored project, or an organization with no return on record, is shown in that context without a page of its own. Search finds organizations that have a page. A group that appears only inside another organization’s filing is not yet searchable; it is shown in that filing’s context.',
      },
      {
        id: 'amended',
        heading: 'Amended returns',
        body: 'When a filer amends a return, the original is kept and marked as replaced; totals count the current return for each period only, so a grant repeated on an amended return is counted once.',
      },
      {
        id: 'subjects',
        heading: 'Subjects',
        body: 'Each organization’s subject comes from the activity code the IRS assigns it (NTEE), mapped into a subject tree. A subject page counts work under it and under every narrower subject, so a grant to a museum counts under arts and culture.',
      },
      {
        id: 'intermediaries',
        heading: 'Intermediaries',
        body: 'Donor-advised fund sponsors, community foundations, and other pass-throughs report grants to thousands of organizations on behalf of their donors. FunderGraph calls a funder an intermediary when it gives to more than a thousand distinct organizations. Intermediaries are shown everywhere and set aside where funders are counted, because sharing an intermediary says nothing about sharing a purpose.',
      },
      {
        id: 'map',
        heading: 'The shared-funder map',
        body: 'Two organizations are neighbours when the same funders gave to both within a three-year window. The map around an organization shows its counted funders and the organizations that share at least two of them, scored by how many. It is computed from the grant rows and nothing else; it describes money, not alignment, and the recipe, its version, its parameters, and the run that produced it are on every map.',
      },
      {
        id: 'search',
        heading: 'Search',
        body: 'The box in the header finds organizations that have a page by the beginnings of the words in their names and cities, folded the way the site reads a name. Organizations that file and that give or receive grants are offered first; a possessive is dropped before the words are compared, and a plural is tried once more without its S. The order of suggestions is a convenience, not a fact about the organizations.',
      },
      {
        id: 'freshness',
        heading: 'Freshness',
        body: 'The graph is built from the returns the IRS had released when it was built; the date and the latest period are on the home page. A filing can lag its fiscal year by a year or more, so the most recent year is always incomplete.',
      },
    ] as readonly { readonly id: ReadingSection; readonly heading: string; readonly body: string }[],
  },
} as const;
